import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { X, Inbox, History, Check, Ban, Loader2, ArrowDownLeft, ArrowUpRight, Search, Send, UserPlus } from 'lucide-react';
import {
    getFamilyRequests, respondToFamilyRequest,
    searchFamilyCandidates, sendFamilyRequest, clearCandidateSearch,
} from '../../redux/familySlice';

// "Will you join my family account?" — the inbox side.
//
// Accepting lets another household read and manage your medical history, so
// this screen exists to make that a decision you take rather than something
// that happens to you. Two tabs:
//
//   Requests — what is waiting on YOU, with Accept / Reject
//   History  — every request either way and what happened to it
//
// History is not decoration. If access is ever questioned — who let this
// household see these records, and when — this is the record of it, visible to
// both sides rather than only in a log nobody can read.
export default function FamilyRequests({ isOpen, onClose }) {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { requestsPending, requestsHistory, requestsLoading, relationships, searchResults, searching } =
        useSelector((s) => s.family || {});
    // RAISING a request lives here now, not in Add.
    //
    // Adding a NEW person and ASKING an existing one are different acts: one
    // creates somebody who has no records and needs nobody's permission, the
    // other needs their consent. Two tabs inside "Add" implied they were
    // variations of the same thing.
    const [tab, setTab] = useState('raise');
    const [query, setQuery] = useState('');
    const [picked, setPicked] = useState(null);
    const [rel, setRel] = useState('');
    const [perm, setPerm] = useState('read');
    const [sending, setSending] = useState(false);
    const [busy, setBusy] = useState(null);
    // Which request is showing its "are you sure" panel. Kept INSIDE this modal
    // rather than opened as an alert on top of it — a dialog stacked on a dialog
    // buries the thing you are deciding about.
    const [confirming, setConfirming] = useState(null);

    useEffect(() => {
        if (!isOpen) return;
        dispatch(getFamilyRequests());
        setQuery(''); setPicked(null); setRel(''); setPerm('read');
        dispatch(clearCandidateSearch());
    }, [isOpen, dispatch]);

    // Debounced: typing a name should not be one request per keystroke. The
    // server ignores anything under 3 characters, which is what stops the
    // directory being walked letter by letter.
    useEffect(() => {
        if (!isOpen || tab !== 'raise') return undefined;
        const q = query.trim();
        if (q.length < 3) { dispatch(clearCandidateSearch()); return undefined; }
        const id = setTimeout(() => dispatch(searchFamilyCandidates(q)), 350);
        return () => clearTimeout(id);
    }, [query, tab, isOpen, dispatch]);

    const raise = async () => {
        if (!picked) return;
        if (!rel) { toast.error(t('family.relationship_required', 'Please choose the relationship')); return; }
        setSending(true);
        try {
            await dispatch(sendFamilyRequest({ toPatientId: picked.patientId, relationship: rel, permission: perm })).unwrap();
            toast.success(t('family.request_sent', 'Request sent. They will see it in their account and by email.'));
            setQuery(''); setPicked(null); setRel(''); setPerm('read');
            dispatch(clearCandidateSearch());
            setTab('mine');
        } catch (err) {
            const msg = err?.en || err?.message?.en || (typeof err === 'string' ? err : null);
            toast.error(msg || t('family.request_failed', 'Could not send the request'));
        } finally { setSending(false); }
    };

    if (!isOpen) return null;

    const pending = requestsPending || [];
    // Sent by me and still unanswered. It belongs beside the incoming ones —
    // "my requests" means both halves — and it must NOT sit in History, which is
    // for things that are finished.
    const outgoing = (requestsHistory || []).filter((r) => r.direction === 'outgoing' && r.status === 'pending');
    const history = (requestsHistory || []).filter((r) => !(r.direction === 'outgoing' && r.status === 'pending'));

    const respond = async (req, accept) => {
        setConfirming(null);
        setBusy(req.requestId);
        try {
            const res = await dispatch(respondToFamilyRequest({ requestId: req.requestId, accept })).unwrap();
            toast.success(res?.message?.en
                || (accept ? t('family.joined', 'You have joined the family account')
                           : t('family.declined', 'Request declined')));
            // Joining changes whose records this login reaches, so every cached
            // screen is now describing the wrong household.
            if (accept) setTimeout(() => window.location.reload(), 900);
        } catch (err) {
            const msg = err?.en || err?.message?.en || (typeof err === 'string' ? err : null);
            toast.error(msg || t('family.action_failed', 'That did not work'));
        } finally { setBusy(null); }
    };

    const daysLeft = (iso) => {
        const d = Math.ceil((new Date(iso) - Date.now()) / 86400000);
        // `count` rather than a template default: i18next needs the number to
        // choose between family.expires_in_one and _other, and a default built
        // with `${d}` could never be translated — the French bundle would have
        // no way to put the number anywhere but where English puts it.
        return d <= 0 ? t('family.expires_today', 'expires today')
                      : t('family.expires_in', { count: d, defaultValue: `${d} day${d === 1 ? '' : 's'} left` });
    };

    const STATUS = {
        accepted: { label: t('family.accepted', 'Accepted'), cls: 'bg-emerald-50 text-emerald-700' },
        declined: { label: t('family.declined_s', 'Declined'), cls: 'bg-red-50 text-red-600' },
        expired: { label: t('family.expired', 'Expired'), cls: 'bg-gray-100 text-gray-500' },
        cancelled: { label: t('family.cancelled', 'Cancelled'), cls: 'bg-gray-100 text-gray-500' },
        pending: { label: t('family.waiting', 'Waiting'), cls: 'bg-amber-50 text-amber-700' },
    };

    const tabBtn = (key, icon, label, count) => (
        <button type="button" onClick={() => setTab(key)}
            className={`flex flex-1 items-center justify-center gap-1.5 border-b-2 py-2.5 text-xs font-semibold cursor-pointer transition ${
                tab === key ? 'border-[#4ABDBD] text-[#3AA8A8]' : 'border-transparent text-gray-400 hover:text-gray-600'}`}>
            {icon}{label}
            {count > 0 && (
                <span className="ml-0.5 rounded-full bg-[#4ABDBD] px-1.5 py-px text-[10px] font-bold text-white">
                    {count}
                </span>
            )}
        </button>
    );

    return createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4">
            <div data-testid="family-requests-modal" className="w-full max-w-md rounded-2xl bg-white shadow-xl">
                <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
                    <h3 className="flex items-center gap-2 font-bold text-[#1a365d]">
                        <Inbox className="h-4 w-4 text-[#4ABDBD]" />
                        {t('family.requests', 'Family requests')}
                    </h3>
                    <button type="button" onClick={onClose} className="text-gray-400 hover:text-red-500 cursor-pointer">
                        <X className="h-5 w-5" />
                    </button>
                </div>

                <div className="flex border-b border-gray-100">
                    {tabBtn('raise', <UserPlus className="h-3.5 w-3.5" />, t('family.tab_raise', 'Raise request'), 0)}
                    {tabBtn('mine', <Inbox className="h-3.5 w-3.5" />, t('family.tab_mine', 'My requests'), pending.length)}
                    {tabBtn('history', <History className="h-3.5 w-3.5" />, t('family.tab_history', 'History'), 0)}
                </div>

                <div className="max-h-[55vh] overflow-y-auto px-4 py-3">
                    {requestsLoading && (
                        <div className="py-10 text-center">
                            <Loader2 className="mx-auto h-5 w-5 animate-spin text-[#4ABDBD]" />
                        </div>
                    )}

                    {/* ---- RAISE ---- asking somebody who already has an account. */}
                    {tab === 'raise' && (
                        <div className="flex flex-col gap-3">
                            <p className="rounded-xl bg-gray-50 px-3 py-2 text-[11px] leading-relaxed text-gray-600">
                                {t('family.invite_hint',
                                    'This person already has their own account. They decide — we send them a request, and nothing changes unless they accept.')}
                            </p>

                            <div className="relative">
                                <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
                                <input
                                    data-testid="candidate-search"
                                    value={query}
                                    onChange={(e) => { setQuery(e.target.value); setPicked(null); }}
                                    placeholder={t('family.search_placeholder', 'Search by name, email or mobile')}
                                    className="w-full rounded-xl border border-gray-300 py-2 pl-8 pr-3 text-sm outline-none focus:border-[#4ABDBD]"
                                />
                            </div>

                            {query.trim().length > 0 && query.trim().length < 3 && (
                                <p className="text-[11px] text-gray-400">
                                    {t('family.search_min', 'Type at least 3 characters.')}
                                </p>
                            )}

                            {searching && <Loader2 className="mx-auto h-4 w-4 animate-spin text-[#4ABDBD]" />}

                            {!searching && query.trim().length >= 3 && (searchResults || []).length === 0 && (
                                <p className="py-4 text-center text-[11px] text-gray-400">
                                    {t('family.search_none', 'Nobody found. Check the spelling.')}
                                </p>
                            )}

                            <div className="flex max-h-44 flex-col gap-1.5 overflow-y-auto">
                                {(searchResults || []).map((c) => {
                                    const chosen = picked?.patientId === c.patientId;
                                    return (
                                        <button key={c.patientId} type="button" data-testid={`candidate-${c.patientId}`}
                                            onClick={() => {
                                                setPicked(c);
                                                // Holding read leaves only ONE thing to ask for, so
                                                // preselect it rather than offering a level the
                                                // server would refuse.
                                                if (c.youHave === 'read') setPerm('readwrite');
                                            }}
                                            className={`flex cursor-pointer items-center gap-3 rounded-xl border px-3 py-2 text-left transition ${
                                                chosen ? 'border-[#4ABDBD] bg-[#4ABDBD]/10' : 'border-gray-200 hover:bg-gray-50'}`}>
                                            <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-[#4ABDBD]/15 text-xs font-bold text-[#3AA8A8]">
                                                {(c.name || '?').charAt(0).toUpperCase()}
                                            </span>
                                            <span className="min-w-0 flex-1">
                                                <span className="block truncate text-sm font-semibold text-gray-800">{c.name}</span>
                                                {/* MASKED BY THE SERVER: enough to recognise the
                                                    right person, never enough to harvest. */}
                                                <span className="block truncate text-[11px] text-gray-500">
                                                    {[c.email, c.phone].filter(Boolean).join(' · ') || '—'}
                                                </span>
                                                {c.youHave === 'read' && (
                                                    <span className="mt-0.5 block text-[10px] font-semibold text-[#3AA8A8]">
                                                        {t('family.you_have_read', 'You have Read — ask for Read & write')}
                                                    </span>
                                                )}
                                            </span>
                                        </button>
                                    );
                                })}
                            </div>

                            {picked && (
                                <>
                                    <div>
                                        <label className="text-xs font-medium text-slate-500">
                                            {t('family.relationship', 'Relationship')}
                                        </label>
                                        <select data-testid="invite-relationship" value={rel}
                                            onChange={(e) => setRel(e.target.value)}
                                            className="tt-select mt-1 w-full rounded-xl border border-gray-300 bg-white p-2 text-sm outline-none focus:border-[#4ABDBD]">
                                            <option value="">{t('family.select', 'Select')}</option>
                                            {(relationships || []).map((r) => (
                                                <option key={r} value={r}>{r}</option>
                                            ))}
                                        </select>
                                    </div>

                                    <div>
                                        <label className="text-xs font-medium text-slate-500">
                                            {t('family.what_access', 'What access are you asking for?')}
                                        </label>
                                        <div className="mt-1 flex gap-2">
                                            {picked.youHave !== 'read' && (
                                                <button type="button" data-testid="ask-read" onClick={() => setPerm('read')}
                                                    className={`flex-1 cursor-pointer rounded-xl border py-2 text-[11px] font-semibold transition ${
                                                        perm === 'read' ? 'border-[#4ABDBD] bg-[#4ABDBD]/10 text-[#3AA8A8]'
                                                                        : 'border-gray-300 text-gray-500 hover:bg-gray-50'}`}>
                                                    {t('family.read_only', 'Read only')}
                                                </button>
                                            )}
                                            <button type="button" data-testid="ask-readwrite" onClick={() => setPerm('readwrite')}
                                                className={`flex-1 cursor-pointer rounded-xl border py-2 text-[11px] font-semibold transition ${
                                                    perm === 'readwrite' ? 'border-[#4ABDBD] bg-[#4ABDBD]/10 text-[#3AA8A8]'
                                                                         : 'border-gray-300 text-gray-500 hover:bg-gray-50'}`}>
                                                {t('family.read_write', 'Read & write')}
                                            </button>
                                        </div>
                                        <p className="mt-1.5 text-[10px] leading-relaxed text-gray-400">
                                            {perm === 'read'
                                                ? t('family.ask_read_note', 'You would be able to see their records, but not change anything.')
                                                : t('family.ask_rw_note', 'You would be able to see their records and book, cancel and upload for them.')}
                                        </p>
                                    </div>

                                    <button type="button" data-testid="send-request" onClick={raise}
                                        disabled={sending || !rel}
                                        className="inline-flex w-full cursor-pointer items-center justify-center gap-1.5 rounded-xl bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] py-2.5 text-sm font-semibold text-white transition hover:shadow-md disabled:opacity-60">
                                        {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                                        {t('family.send_request', 'Send request')}
                                    </button>
                                </>
                            )}
                        </div>
                    )}

                    {!requestsLoading && tab === 'mine' && pending.length === 0 && outgoing.length === 0 && (
                        <p className="py-10 text-center text-xs text-gray-400">
                            {t('family.no_requests', 'Nothing is waiting for your answer.')}
                        </p>
                    )}

                    {!requestsLoading && tab === 'mine' && pending.map((r) => (
                        <div key={r.requestId} data-testid={`request-${r.requestId}`}
                            className="mb-2 rounded-xl border border-gray-200 p-3">
                            <p className="text-sm font-semibold text-gray-800">{r.fromName}</p>
                            <p className="mt-0.5 text-xs text-gray-500">
                                {t('family.wants_to_add', {
                                    relationship: r.relationship || 'a member',
                                    defaultValue: `wants to add you to their family account as ${r.relationship || 'a member'}.`,
                                })}
                            </p>
                            {/* Said plainly, before the buttons — this is what the
                                decision actually is. */}
                            {/* WHAT they are asking for, in the words of the
                                consequence rather than the permission name.
                                "Read" means nothing until it is spelled out as
                                what the other person will be able to do. */}
                            <p className="mt-2 rounded-lg bg-amber-50 px-2.5 py-1.5 text-[11px] text-amber-800">
                                {r.permission === 'readwrite'
                                    ? t('family.access_warning_rw',
                                        'If you accept, they will be able to see your health records AND book, cancel and upload for you.')
                                    : t('family.access_warning_r',
                                        'If you accept, they will be able to see your health records. They will not be able to change anything.')}
                            </p>
                            <p className="mt-1.5 text-[10px] text-gray-400">{daysLeft(r.expiresAt)}</p>

                            {/* Accepting IS the consent, so it is stated in terms
                                of what they gain — not "are you sure?". It opens
                                here, in the card, so the request stays readable
                                while it is being decided. */}
                            {confirming === r.requestId && (
                                <div className="mt-2.5 rounded-lg border border-[#4ABDBD]/40 bg-[#4ABDBD]/10 p-3">
                                    <p className="text-xs font-bold text-[#1a365d]">
                                        {t('family.accept_title', 'Join this family account?')}
                                    </p>
                                    <p className="mt-1 text-[11px] leading-relaxed text-gray-700">
                                        <b>{r.fromName}</b>{' '}
                                        {r.permission === 'readwrite'
                                            ? t('family.accept_body_rw',
                                               'would be able to see your health records and act on your behalf — booking, cancelling and uploading. You can change or remove this at any time.')
                                            : t('family.accept_body_r',
                                               'would be able to see your health records, but not change anything. You can remove this at any time.')}
                                    </p>
                                    <div className="mt-2.5 flex gap-2">
                                        <button type="button" data-testid={`accept-confirm-${r.requestId}`}
                                            disabled={busy === r.requestId} onClick={() => respond(r, true)}
                                            className="flex-1 cursor-pointer rounded-lg bg-[#4ABDBD] py-1.5 text-[11px] font-semibold text-white hover:bg-[#3AA8A8] disabled:opacity-50">
                                            {busy === r.requestId
                                                ? <Loader2 className="mx-auto h-3.5 w-3.5 animate-spin" />
                                                : t('family.accept_yes', 'Yes, accept')}
                                        </button>
                                        <button type="button" disabled={busy === r.requestId}
                                            onClick={() => setConfirming(null)}
                                            className="flex-1 cursor-pointer rounded-lg border border-gray-200 bg-white py-1.5 text-[11px] font-semibold text-gray-600 hover:bg-gray-50 disabled:opacity-50">
                                            {t('common.cancel', 'Cancel')}
                                        </button>
                                    </div>
                                </div>
                            )}

                            {confirming !== r.requestId && (
                            <div className="mt-2.5 flex gap-2">
                                <button type="button" data-testid={`accept-${r.requestId}`}
                                    disabled={busy === r.requestId} onClick={() => setConfirming(r.requestId)}
                                    className="flex flex-1 items-center justify-center gap-1 rounded-lg bg-[#4ABDBD] py-2 text-xs font-semibold text-white hover:bg-[#3AA8A8] disabled:opacity-50 cursor-pointer">
                                    {busy === r.requestId ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                                          : <><Check className="h-3.5 w-3.5" />{t('family.accept', 'Accept')}</>}
                                </button>
                                <button type="button" data-testid={`reject-${r.requestId}`}
                                    disabled={busy === r.requestId} onClick={() => respond(r, false)}
                                    className="flex flex-1 items-center justify-center gap-1 rounded-lg border border-gray-200 py-2 text-xs font-semibold text-gray-600 hover:bg-gray-50 disabled:opacity-50 cursor-pointer">
                                    <Ban className="h-3.5 w-3.5" />{t('family.reject', 'Reject')}
                                </button>
                            </div>
                            )}
                        </div>
                    ))}

                    {!requestsLoading && tab === 'mine' && outgoing.length > 0 && (
                        <>
                            <p className="mb-1.5 mt-3 text-[10px] font-bold uppercase tracking-wide text-gray-400">
                                {t('family.waiting_on_them', 'Waiting for an answer')}
                            </p>
                            {outgoing.map((r) => (
                                <div key={r.requestId} className="mb-1.5 flex items-center gap-2.5 rounded-lg border border-gray-100 px-3 py-2">
                                    <ArrowUpRight className="h-3.5 w-3.5 flex-shrink-0 text-gray-400" />
                                    <span className="min-w-0 flex-1">
                                        <span className="block truncate text-xs font-semibold text-gray-700">{r.toName}</span>
                                        <span className="text-[10px] text-gray-400">
                                            {r.permission === 'readwrite'
                                                ? t('family.read_write', 'Read & write')
                                                : t('family.read_only', 'Read only')}
                                            {' · '}{daysLeft(r.expiresAt)}
                                        </span>
                                    </span>
                                    <span className="flex-shrink-0 rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-semibold text-amber-700">
                                        {t('family.waiting', 'Waiting')}
                                    </span>
                                </div>
                            ))}
                        </>
                    )}

                    {!requestsLoading && tab === 'history' && history.length === 0 && (
                        <p className="py-10 text-center text-xs text-gray-400">
                            {t('family.no_history', 'No requests yet.')}
                        </p>
                    )}

                    {!requestsLoading && tab === 'history' && history.map((r) => {
                        const s = STATUS[r.status] || STATUS.pending;
                        const out = r.direction === 'outgoing';
                        return (
                            <div key={r.requestId} className="mb-1.5 flex items-center gap-2.5 rounded-lg border border-gray-100 px-3 py-2">
                                {out ? <ArrowUpRight className="h-3.5 w-3.5 flex-shrink-0 text-gray-400" />
                                     : <ArrowDownLeft className="h-3.5 w-3.5 flex-shrink-0 text-gray-400" />}
                                <div className="min-w-0 flex-1">
                                    <p className="truncate text-xs font-semibold text-gray-700">
                                        {out ? t('family.you_asked', { name: r.toName, defaultValue: `You asked ${r.toName}` })
                                             : t('family.they_asked', { name: r.fromName, defaultValue: `${r.fromName} asked you` })}
                                    </p>
                                    <p className="text-[10px] text-gray-400">
                                        {new Date(r.respondedAt || r.createdAt).toLocaleDateString()}
                                        {r.relationship ? ` · ${r.relationship}` : ''}
                                    </p>
                                </div>
                                <span className={`flex-shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold ${s.cls}`}>
                                    {s.label}
                                </span>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>,
        document.body
    );
}
